import { CustomPage as CustomPageType, CustomRouteWithPage } from '@/types/pages';

import Link from 'next/link';
import LoadingIndicator from '@/components/LoadingIndicator';
import MainLayout, { OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH } from '@/layouts/MainLayout';
import NotFoundPage from '@/components/404';
import React from 'react';
import { componentSerializer } from '@/utils/componentSerializer';
import { getHeaderOgImageUrl } from '@/utils/sanityImage';
import styles from './styles.module.css';
import { withSiteName } from '@/utils/pageTitle';
import { useRouter } from 'next/router';

export interface CustomPageProps {
  menuData: unknown;
  content: CustomRouteWithPage;
  isDraft: boolean;
  slug: string[] | string;
  footer: unknown;
  ogUrl?: string;
  // Breadcrumb back to the section index. Optional because not every namespace
  // has an index page worth linking; where it does, this is what gives that
  // index an inbound internal link instead of leaving it orphaned (FOX2-173).
  sectionLink?: { href: string; label: string };
}

const CustomPage: React.FC<CustomPageProps> = ({ content, isDraft, menuData, footer, ogUrl, sectionLink }) => {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <div className={styles.loaderContainer}>
        <LoadingIndicator text="Loading" />
      </div>
    );
  }

  if (!content) {
    return <NotFoundPage />;
  }

  const { page, seo } = content;
  const components = componentSerializer(page as CustomPageType);
  // Page-builder routes used to pass '' here, so every route without an SEO title fell
  // through MainLayout to the one sitewide default and they all shared a <title>. Fall
  // back to the page's own name instead, suffixed with the brand the way the hand-built
  // pages do, so an unset title is still page-specific (FOX2-77).
  const metaTitle = seo?.title || withSiteName((page as CustomPageType)?.title) || '';
  // These pages passed no image of their own, so all of them shared the branded default
  // card. The main header image is the page-specific visual an editor has already set, so
  // reuse it as the share image (FOX2-197).
  const ogImage = getHeaderOgImageUrl(page as CustomPageType, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT);
  return (
    <MainLayout
      bgWhite={true}
      navIsFixed={true}
      navIsDark={true}
      ogUrl={ogUrl}
      ogImage={ogImage}
      ogImageWidth={ogImage ? OG_IMAGE_WIDTH : undefined}
      ogImageHeight={ogImage ? OG_IMAGE_HEIGHT : undefined}
      metaTitle={metaTitle}
      menuData={menuData}
      footer={footer}
      metaDescription={seo?.description || ''}
    >
      {sectionLink && (
        <nav aria-label="Breadcrumb" className={styles.sectionLink}>
          <Link href={sectionLink.href}>{sectionLink.label}</Link>
        </nav>
      )}
      <main className={!isDraft ? styles.content : styles.contentDraft}>{components}</main>
    </MainLayout>
  );
};

export default CustomPage;
